import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
/**
 * Component for displaying time remaining until venture end date
 *
 * Requirements:
 * - 4.4: Display time remaining until end date
 * - 9.2: Show countdown in public venture listings
 * - 10.3: Display countdown in featured ventures
 * - 14.1: Handle expired ventures gracefully
 *
 * Features:
 * - Real-time countdown updates
 * - Urgent styling for ventures with < 48 hours remaining
 * - Graceful handling of expired ventures
 * - Responsive sizing for mobile and desktop
 * - Accessible with ARIA labels
 */
export const VentureCountdown = ({ endDate, size = "md", showLabel = true, className = "", }) => {
    // State for time remaining in milliseconds
    const [timeRemaining, setTimeRemaining] = useState(null);
    // Calculate time remaining
    useEffect(() => {
        if (!endDate) {
            setTimeRemaining(null);
            return;
        }
        const calculateTimeRemaining = () => {
            const now = Date.now();
            const end = new Date(endDate).getTime();
            const remaining = end - now;
            // If expired, set to 0
            if (remaining <= 0) {
                setTimeRemaining(0);
                return;
            }
            setTimeRemaining(remaining);
        };
        // Calculate immediately
        calculateTimeRemaining();
        // Update every second for real-time countdown
        const interval = setInterval(calculateTimeRemaining, 1000);
        return () => clearInterval(interval);
    }, [endDate]);
    // Format time remaining into human-readable string
    const formatTimeRemaining = (ms) => {
        if (ms <= 0) {
            return "Expired";
        }
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        if (days > 0) {
            return `${days} day${days !== 1 ? "s" : ""} left`;
        }
        if (hours > 0) {
            return `${hours} hour${hours !== 1 ? "s" : ""} left`;
        }
        if (minutes > 0) {
            return `${minutes} minute${minutes !== 1 ? "s" : ""} left`;
        }
        return `${seconds} second${seconds !== 1 ? "s" : ""} left`;
    };
    // Check if venture is urgent (< 48 hours remaining)
    const isUrgent = (ms) => {
        const FORTY_EIGHT_HOURS = 48 * 60 * 60 * 1000;
        return ms > 0 && ms < FORTY_EIGHT_HOURS;
    };
    // Size-based styling
    const sizeStyles = {
        sm: "text-xs",
        md: "text-sm",
        lg: "text-base",
    };
    // Handle no end date
    if (!endDate) {
        return (_jsx("div", { className: `${sizeStyles[size]} text-gray-500 ${className}`, role: "text", "aria-label": "No end date", children: showLabel && _jsx("span", { children: "No deadline" }) }));
    }
    // Handle loading state
    if (timeRemaining === null) {
        return (_jsx("div", { className: `${sizeStyles[size]} text-gray-500 ${className}`, role: "text", "aria-label": "Loading countdown", children: showLabel && _jsx("span", { children: "Loading..." }) }));
    }
    const expired = timeRemaining === 0;
    const urgent = isUrgent(timeRemaining);
    const formattedTime = formatTimeRemaining(timeRemaining);
    // Determine styling based on state
    const getTextColor = () => {
        if (expired) {
            return "text-gray-500";
        }
        if (urgent) {
            return "text-red-600 font-semibold";
        }
        return "text-gray-700";
    };
    // Determine icon based on state
    const getIcon = () => {
        if (expired) {
            return "⏱️";
        }
        if (urgent) {
            return "⚠️";
        }
        return "⏰";
    };
    return (_jsxs("div", { className: `inline-flex items-center gap-1.5 ${sizeStyles[size]} ${getTextColor()} ${className}`, role: "timer", "aria-live": "polite", "aria-label": expired ? "Venture expired" : `Time remaining: ${formattedTime}`, children: [_jsx("span", { "aria-hidden": "true", children: getIcon() }), _jsx("span", { className: urgent ? "animate-pulse" : "", children: formattedTime })] }));
};
